/* ------------------------------------------------------------------
 * Shipping Estimator — postcode → JS Rates shipping options
 *
 * Calls /apps/jsrates/rates (Shopify auto-proxied, no auth needed).
 * Shows service names + descriptions only (no prices).
 * Caches results in localStorage so repeat postcodes are instant.
 * Vanilla JS — no jQuery dependency.
 * ------------------------------------------------------------------ */
(function () {
  'use strict';

  var CACHE_KEY_PREFIX = 'se_rates_';
  var CACHE_MAX_AGE = 4 * 60 * 60 * 1000; // 4 hours
  var EXCLUDED_SERVICES = ['Store at Different Drop and Deliver Later'];
  var ORIGIN = {
    country: 'AU',
    postal_code: '2007',
    province: 'NSW',
    city: 'Ultimo',
    address1: '3 Fig St',
    company_name: 'Different Drop'
  };

  // Representative single-bottle payload (page has no product context).
  // Service options don't meaningfully change at the 1-bottle level.
  var REPRESENTATIVE_ITEM = {
    quantity: 1,
    grams: 1500,
    price: 3000,
    requires_shipping: true
  };

  var activeController = null;

  function isValidPostcode(val) {
    return /^\d{4}$/.test(val);
  }

  function getCached(postcode) {
    try {
      var raw = localStorage.getItem(CACHE_KEY_PREFIX + postcode);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      // New format: { rates: [...], ts: 1234567890 }. Old format: bare array — treat as expired.
      if (!entry || !entry.ts) return null;
      if (Date.now() - entry.ts > CACHE_MAX_AGE) return null;
      return entry.rates || null;
    } catch (e) {
      return null;
    }
  }

  function setCache(postcode, rates) {
    try {
      localStorage.setItem(CACHE_KEY_PREFIX + postcode, JSON.stringify({ rates: rates, ts: Date.now() }));
    } catch (e) { /* storage full — silently ignore */ }
  }

  function buildPayload(postcode) {
    return {
      rate: {
        origin: ORIGIN,
        destination: { country: 'AU', postal_code: postcode },
        items: [REPRESENTATIVE_ITEM],
        currency: 'AUD',
        locale: 'en'
      }
    };
  }

  function fetchRates(postcode) {
    // Cancel any in-flight request
    if (activeController) {
      activeController.abort();
    }
    activeController = new AbortController();

    return fetch('/apps/jsrates/rates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload(postcode)),
      signal: activeController.signal
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var rates = (data && data.rates) ? data.rates : [];
        // Filter out excluded services and those with no service_name
        return rates.filter(function (r) {
          var name = (r.service_name || '').trim();
          return name && EXCLUDED_SERVICES.indexOf(name) === -1;
        });
      });
  }

  // ---- DOM helpers ---------------------------------------------------

  function showLoading(statusEl) {
    statusEl.innerHTML =
      '<div class="shipping-estimator__loading">' +
        '<span class="shipping-estimator__dot"></span>' +
        '<span class="shipping-estimator__dot"></span>' +
        '<span class="shipping-estimator__dot"></span>' +
      '</div>';
    statusEl.hidden = false;
  }

  function showError(statusEl, msg) {
    statusEl.innerHTML = '<p class="shipping-estimator__status shipping-estimator__error">' + msg + '</p>';
    statusEl.hidden = false;
  }

  function hideStatus(statusEl) {
    statusEl.innerHTML = '';
    statusEl.hidden = true;
  }

  function renderRates(resultsEl, rates) {
    if (!rates.length) {
      resultsEl.innerHTML = '<li class="shipping-estimator__rate"><span class="shipping-estimator__service">No delivery options found for this postcode.</span></li>';
      resultsEl.hidden = false;
      return;
    }

    resultsEl.innerHTML = rates.map(function (rate) {
      var desc = (rate.description || '').trim() || '';
      return (
        '<li class="shipping-estimator__rate">' +
          '<span class="shipping-estimator__service">' + escapeHtml(rate.service_name) + '</span>' +
          (desc ? '<span class="shipping-estimator__desc">' + escapeHtml(desc) + '</span>' : '') +
        '</li>'
      );
    }).join('');
    resultsEl.hidden = false;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ---- Init per widget instance -------------------------------------

  function initWidget(root) {
    if (root.__seInit) return;
    root.__seInit = true;

    var entryEl   = root.querySelector('[data-se-entry]');
    var setEl     = root.querySelector('[data-se-set]');
    var noteEl    = root.querySelector('[data-se-note]');
    var statusEl  = root.querySelector('[data-se-status]');
    var resultsEl = root.querySelector('[data-se-results]');
    var inputEl   = root.querySelector('[data-se-postcode]');
    var checkBtn  = root.querySelector('[data-se-check]');
    var changeBtn = root.querySelector('[data-se-change]');

    if (!entryEl || !inputEl || !checkBtn || !resultsEl) return;

    function showEntry() {
      entryEl.hidden = false;
      if (setEl) setEl.hidden = true;
      resultsEl.hidden = true;
      resultsEl.innerHTML = '';
      hideStatus(statusEl);
      inputEl.value = '';
      inputEl.focus();
    }

    function showSet(postcode) {
      entryEl.hidden = true;
      if (setEl && noteEl) {
        noteEl.textContent = 'Delivering to ' + postcode;
        setEl.hidden = false;
      }
    }

    function doEstimate() {
      var postcode = inputEl.value.replace(/\s/g, '');
      if (!isValidPostcode(postcode)) {
        inputEl.setCustomValidity('Please enter a 4-digit Australian postcode');
        inputEl.reportValidity();
        return;
      }
      inputEl.setCustomValidity('');

      // Check cache first
      var cached = getCached(postcode);
      if (cached) {
        renderRates(resultsEl, cached);
        showSet(postcode);
        return;
      }

      // Show loading, disable button
      checkBtn.disabled = true;
      showLoading(statusEl);
      resultsEl.hidden = true;

      fetchRates(postcode)
        .then(function (rates) {
          setCache(postcode, rates);
          // Save postcode for cross-widget sharing (PDP delivery estimate reads this)
          try { localStorage.setItem('se_postcode', postcode); } catch (e) {}
          hideStatus(statusEl);
          renderRates(resultsEl, rates);
          showSet(postcode);
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return; // user cancelled
          hideStatus(statusEl);
          showError(statusEl, 'We couldn\u2019t fetch delivery options right now. Please try again or see the FAQ below.');
        })
        .finally(function () {
          checkBtn.disabled = false;
        });
    }

    checkBtn.addEventListener('click', doEstimate);
    inputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        doEstimate();
      }
    });
    if (changeBtn) {
      changeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        showEntry();
      });
    }
  }

  // ---- Bootstrap -----------------------------------------------------

  function initAll() {
    document.querySelectorAll('[data-shipping-estimator]').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
